/**
 * feat-298 — helpers shared by every per-item card on `/quiz-review`.
 *
 * Moved verbatim out of `QuizContentReview.tsx` (spec §6). One module, not a
 * copy per card, so the feat-284 markers cannot drift between card types.
 */
import React from 'react';
import { languageLabel } from '../../../services/translationsAdminService';
import { QUIZ_REVIEW_LANGUAGE_COPY } from '../QuizReviewLanguageControl';
import { ResolvedField } from '../quizReviewLanguage';

export { resolveField } from '../quizReviewLanguage';
export type { ResolvedField } from '../quizReviewLanguage';

export const OPTION_LETTERS = ['A', 'B', 'C', 'D'] as const;

// ============================================================
// feat-284 — the two markers
// ============================================================

/**
 * `column` is the language a rendered cell is written in: `null` for canonical
 * English (EN mode, and the left column of Both), or a language code for the
 * translated side. Every item renderer takes one and passes it to
 * {@link resolveField}; nothing below decides on its own whether to translate.
 */
export type Column = string | null;

export const CHIP_BASE =
  'inline-block align-middle ml-1.5 text-[10px] font-semibold leading-none ' +
  'tracking-wide uppercase px-1.5 py-[3px] rounded';

/**
 * The untranslated / stale chip.
 *
 * `rounded` (4 px), never `rounded-full`: the two markers differ on three axes —
 * hue (cool slate vs warm amber), silhouette, and glyph (a 2-letter code vs an
 * English word) — so confusing them requires missing all three, which is also
 * the colour-blind-safety argument (design notes §5).
 *
 * `not-italic` on the stale chip is load-bearing: the 💡 and 🎉 explanation lines
 * are `italic`, and an italic chip reads as a typo.
 *
 * There is deliberately NO chip for `fresh` vs `approved`. That distinction is
 * the `/quiz-translations` queue's, and surfacing it here would turn a reading
 * surface into a second queue.
 */
export const FieldChip: React.FC<{ marker: 'untranslated' | 'stale'; language: string }> = ({
  marker,
  language,
}) =>
  marker === 'untranslated' ? (
    <span
      className={`${CHIP_BASE} bg-preview-light text-preview ring-1 ring-preview-border`}
      title={QUIZ_REVIEW_LANGUAGE_COPY.untranslatedTitle(languageLabel(language))}
    >
      {QUIZ_REVIEW_LANGUAGE_COPY.untranslatedChip}
    </span>
  ) : (
    <span
      className={`${CHIP_BASE} not-italic bg-attention-surface text-attention-on ring-1 ring-attention-line`}
      title={QUIZ_REVIEW_LANGUAGE_COPY.staleTitle}
    >
      {QUIZ_REVIEW_LANGUAGE_COPY.staleChip}
    </span>
  );

/**
 * One resolved field: the text run, then its marker.
 *
 * AFTER the run, never before. Before-the-text reads as a label for a column;
 * after-the-text reads as a footnote on that specific string, which is what it
 * is. One chip per FIELD — a question with three untranslated options gets three.
 *
 * `{f.text}` is a JSX TEXT NODE. `content_translation.translated_text` is
 * untrusted (ADR 0004 D4 / C5b): a translation containing `<script>` renders as
 * those literal characters. No `dangerouslySetInnerHTML` anywhere in this file.
 *
 * The English run it marks renders at NORMAL ink — not dimmed, not italicised.
 * The reviewer has to read that text; the chip says where it came from, not
 * "skip this".
 */
export const FieldRun: React.FC<{ f: ResolvedField; language: string; className?: string }> = ({
  f,
  language,
  className,
}) => (
  <>
    {f.lang || className ? (
      <span lang={f.lang ?? undefined} className={className}>
        {f.text}
      </span>
    ) : (
      f.text
    )}
    {f.marker !== 'none' && <FieldChip marker={f.marker} language={language} />}
  </>
);

/** The 2 px amber left rule that makes a stale BLOCK field findable while
 *  scrolling fast (design notes §5). Inline fields — an option, a criterion
 *  label — get the chip only; a left rule on an `<li>` fights the bullet. */
export function staleRule(f: ResolvedField): string {
  return f.marker === 'stale' ? ' border-l-2 border-attention-line' : '';
}
